import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  User, FileText, Package, Star, Gift, Copy, Share2,
  TrendingUp, Users, Wallet, ChevronRight, ArrowUpRight,
  Download, ShoppingBag, Euro, CheckCircle, Clock, Truck,
  X, AlertCircle, Calendar, Award, Percent, Settings,
  LogOut, Edit2, Save, BarChart2, MapPin, Building2, Heart, Shield
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { formatDateFR } from '../utils/dateFormat';
import { openProtectedDoc } from '../utils/docLinks';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { formatDate, formatDateWithDay } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PALIERS = [
  { min: 0,  pct: 0,  label: 'Nouveau',   color: '#5DBCBF', icon: '🌱' },
  { min: 2,  pct: 5,  label: 'Bronze',     color: '#CD7F32', icon: '🥉' },
  { min: 5,  pct: 10, label: 'Silver',     color: '#9CA3AF', icon: '🥈' },
  { min: 10, pct: 15, label: 'Gold',       color: '#D4AF37', icon: '🏆' },
  { min: 20, pct: 20, label: 'Platine',    color: '#E5E7EB', icon: '⭐⭐⭐' },
];

const STATUT_CONFIG = {
  brouillon:        { label: 'Brouillon',        color: '#888', bg: 'rgba(136,136,136,0.08)', icon: <Clock size={12}/> },
  envoye:           { label: 'Envoyé',            color: '#7DB1FF', bg: 'rgba(125,177,255,0.1)', icon: <FileText size={12}/> },
  signe:            { label: 'Signé ✓',           color: '#1F9D7A', bg: 'rgba(31,157,122,0.1)', icon: <CheckCircle size={12}/> },
  acompte_paye:     { label: 'Acompte payé ✓',    color: '#1F9D7A', bg: 'rgba(31,157,122,0.1)', icon: <CheckCircle size={12}/> },
  solde_paye:       { label: 'Soldé ✓',           color: '#D4AF37', bg: 'rgba(212,175,55,0.1)', icon: <CheckCircle size={12}/> },
  en_livraison:     { label: 'En livraison 🚛',   color: '#5DBCBF', bg: 'rgba(93,188,191,0.1)', icon: <Truck size={12}/> },
  livre:            { label: 'Livré ✓',           color: '#1F9D7A', bg: 'rgba(31,157,122,0.1)', icon: <CheckCircle size={12}/> },
  termine:          { label: 'Terminé',           color: '#1F9D7A', bg: 'rgba(31,157,122,0.06)', icon: <CheckCircle size={12}/> },
  annule:           { label: 'Annulé',            color: '#e53e3e', bg: 'rgba(229,62,62,0.08)', icon: <X size={12}/> },
  en_attente:       { label: 'En attente',        color: '#C58B19', bg: 'rgba(197,139,25,0.08)', icon: <Clock size={12}/> },
};

const ORDER_STATUT = {
  en_attente_paiement: { label: 'En attente', color: '#C58B19', bg: 'rgba(197,139,25,0.08)' },
  paye:                { label: 'Payé ✓',     color: '#1F9D7A', bg: 'rgba(31,157,122,0.1)' },
  expedie:             { label: 'Expédié 🚛', color: '#5DBCBF', bg: 'rgba(93,188,191,0.1)' },
  livre:               { label: 'Livré ✓',    color: '#1F9D7A', bg: 'rgba(31,157,122,0.1)' },
  annule:              { label: 'Annulé',      color: '#e53e3e', bg: 'rgba(229,62,62,0.08)' },
};

function canInvoice(statut) { return ['acompte_paye','solde_paye','livre','termine'].includes(statut); }
function canOrderInvoice(statut) { return ['paye','expedie','livre'].includes(statut); }

// ═══════════════════════════════════════════════════════════════════════════
// ANO-ECC-07 — Espace client UNIQUE, adapté au profil (ex- /espace-pro & /espace-association)
// La remise et les avantages affichés dépendent du profil de l'utilisateur.
// ═══════════════════════════════════════════════════════════════════════════
const PROFILE_PERKS = {
  entreprise: {
    label: 'Entreprise', remise: 15, color: '#D4AF37', icon: <Building2 size={22}/>,
    avantages: [
      { icon: <Percent size={18}/>, color: '#D4AF37', title: '-15% sur toutes vos locations', desc: 'Tarif Entreprise appliqué automatiquement à votre connexion' },
      { icon: <FileText size={18}/>, color: '#5DBCBF', title: 'Devis professionnels sous 24h', desc: 'Avec mentions légales, numéro SIRET, TVA intracommunautaire' },
      { icon: <Download size={18}/>, color: '#1F9D7A', title: 'Factures téléchargeables', desc: 'Toutes vos factures en PDF, imputables en comptabilité' },
      { icon: <Users size={18}/>, color: '#7DB1FF', title: 'Interlocuteur dédié', desc: 'Un contact direct pour vos événements récurrents' },
      { icon: <Star size={18}/>, color: '#C58B19', title: 'Programme fidélité Business', desc: 'Cashback 5% + remise palier cumulée avec votre tarif pro' },
      { icon: <Shield size={18}/>, color: '#8B5CF6', title: 'Codes CE pour vos employés', desc: 'Créez des codes de réduction pour vos salariés' },
    ],
  },
  ce: {
    label: 'CE', remise: 10, color: '#C58B19', icon: <Building2 size={22}/>,
    avantages: [
      { icon: <Percent size={18}/>, color: '#C58B19', title: '-10% sur toutes les locations', desc: 'Tarif CE appliqué automatiquement dès votre connexion' },
      { icon: <Gift size={18}/>, color: '#1F9D7A', title: 'Offres exclusives CE', desc: 'Accès prioritaire aux offres du weekend et packs spéciaux' },
      { icon: <FileText size={18}/>, color: '#5DBCBF', title: 'Facturation simplifiée', desc: 'Factures en PDF pour votre CSE' },
      { icon: <Users size={18}/>, color: '#7DB1FF', title: "Jusqu'à 50 bénéficiaires", desc: 'Partagez votre code CE avec vos collègues' },
    ],
  },
  association: {
    label: 'Association', remise: 15, color: '#7DB1FF', icon: <Heart size={22}/>,
    avantages: [
      { icon: <Percent size={18}/>, color: '#7DB1FF', title: '-15% sur toutes vos locations', desc: 'Tarif association appliqué automatiquement à votre connexion' },
      { icon: <FileText size={18}/>, color: '#5DBCBF', title: 'Devis adaptés associations', desc: 'Avec vos coordonnées et numéro RNA/SIREN associatif' },
      { icon: <Download size={18}/>, color: '#1F9D7A', title: 'Factures pour votre comptabilité', desc: 'Factures PDF conformes pour votre trésorerie associative' },
      { icon: <Calendar size={18}/>, color: '#D4AF37', title: 'Priorité sur les disponibilités', desc: 'Réservation anticipée pour vos fêtes annuelles et AG' },
      { icon: <Star size={18}/>, color: '#C58B19', title: 'Cashback 5% sur chaque location', desc: 'Crédits utilisables sur votre prochaine réservation' },
      { icon: <Users size={18}/>, color: '#8B5CF6', title: 'Tarifs avantageux pour les membres', desc: 'Partagez votre code avec les membres de votre association' },
    ],
  },
};

function TabAvantages({ perks }) {
  if (!perks) return null;
  return (
    <div className="space-y-5" data-testid="tab-avantages-content">
      <div className="p-4 rounded-2xl" style={{ background: `${perks.color}08`, border: `1px solid ${perks.color}25` }}>
        <div className="flex items-center gap-3">
          <Percent size={24} style={{ color: perks.color, flexShrink: 0 }} />
          <div>
            <p className="font-bold" style={{ color: 'var(--ink)' }}>Votre remise {perks.label} : -{perks.remise}%</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Appliquée automatiquement sur toutes vos locations quand vous êtes connecté.</p>
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {perks.avantages.map((av, i) => (
          <div key={i} className="flex gap-3 p-4 rounded-2xl" style={{ background: '#fff', border: `1px solid ${av.color}20` }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${av.color}12`, color: av.color }}>{av.icon}</div>
            <div>
              <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{av.title}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>{av.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 rounded-2xl" style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.25)' }}>
        <p className="font-semibold text-sm mb-1" style={{ color: 'var(--ink)' }}>Besoin d'un devis spécifique ?</p>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Nos équipes élaborent des offres sur mesure pour vos événements.</p>
        <Link to="/contact" className="inline-flex items-center gap-2 mt-3 text-sm font-semibold" style={{ color: 'var(--teal)' }}>
          Nous contacter <ArrowUpRight size={14}/>
        </Link>
      </div>
    </div>
  );
}

// ─── Carte Fidélité Visuelle ────────────────────────────────────────────────
function CarteFidelite({ fidelite, loading }) {
  if (loading) return <Skeleton className="h-48 rounded-2xl" />;
  const pal = fidelite?.palier || PALIERS[0];
  const next = fidelite?.prochain_palier;
  const progress = next ? Math.min(100, Math.round(((fidelite.nb_locations || 0) / next.min_locations) * 100)) : 100;
  const cashback = fidelite?.cashback_disponible || 0;

  return (
    <div className="p-5 rounded-2xl" style={{ background: `linear-gradient(135deg, ${pal.color}15, ${pal.color}05)`, border: `1px solid ${pal.color}30` }}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Carte fidélité</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{pal.icon}</span>
            <p className="text-xl font-bold" style={{ color: pal.color }}>{pal.label}</p>
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{fidelite?.nb_locations || 0} location{(fidelite?.nb_locations || 0) > 1 ? 's' : ''}</p>
        </div>
        <div className="text-right">
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Cashback dispo</p>
          <p className="text-2xl font-bold" style={{ color: '#1F9D7A' }}>{cashback.toFixed(2)}€</p>
          {fidelite?.cashback_cumule > 0 && (
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total cumulé : {fidelite.cashback_cumule.toFixed(2)}€</p>
          )}
        </div>
      </div>

      {/* Remise palier */}
      {pal.pct > 0 && (
        <div className="flex items-center gap-2 mb-4 p-2.5 rounded-xl" style={{ background: `${pal.color}12`, border: `1px solid ${pal.color}25` }}>
          <Percent size={14} style={{ color: pal.color }} />
          <p className="text-sm font-semibold" style={{ color: pal.color }}>-{pal.pct}% sur vos locations · palier {pal.label}</p>
        </div>
      )}

      {/* Progression vers le prochain palier */}
      {next && (
        <div>
          <div className="flex justify-between text-xs mb-1.5" style={{ color: 'var(--text-muted)' }}>
            <span>{pal.label}</span>
            <span>{next.icon} {next.label} ({next.min_locations} loc.) — {fidelite?.locations_avant_prochain} restantes</span>
          </div>
          <div className="rounded-full h-2" style={{ background: 'var(--border)' }}>
            <div className="rounded-full h-2 transition-all" style={{ width: `${progress}%`, background: pal.color }} />
          </div>
        </div>
      )}

      <p className="text-xs mt-3" style={{ color: 'var(--text-muted)' }}>5% cashback sur chaque location · cumulable</p>
    </div>
  );
}

// ─── Onglet Dashboard ───────────────────────────────────────────────────────
function TabDashboard({ stats, loading }) {
  const kpis = [
    { icon: <Euro size={18}/>, color: '#D4AF37', label: 'Total dépensé', value: stats ? `${stats.total_global.toFixed(0)}€` : null },
    { icon: <FileText size={18}/>, color: '#5DBCBF', label: 'Réservations', value: stats?.nb_reservations },
    { icon: <ShoppingBag size={18}/>, color: '#1F9D7A', label: 'Commandes boutique', value: stats?.nb_commandes_boutique },
    { icon: <Gift size={18}/>, color: '#C58B19', label: 'Cashback dispo', value: stats ? `${stats.fidelite?.cashback_disponible?.toFixed(2)}€` : null },
  ];

  return (
    <div className="space-y-5">
      {/* KPIs */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {kpis.map((k, i) => (
            <div key={i} className="p-4 rounded-2xl text-center" style={{ background: '#fff', border: `1px solid ${k.color}20` }}>
              <div className="w-9 h-9 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: `${k.color}12`, color: k.color }}>{k.icon}</div>
              <p className="text-lg font-bold" style={{ color: k.color }}>{k.value ?? '—'}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{k.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Prochain événement */}
      {stats?.prochain_evenement && (
        <div className="p-4 rounded-2xl flex items-center gap-4" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
          <Calendar size={28} style={{ color: 'var(--teal)', flexShrink: 0 }} />
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Prochain événement</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {formatDateWithDay(stats.prochain_evenement.date + 'T00:00:00')}
              {stats.prochain_evenement.total > 0 && ` · ${stats.prochain_evenement.total.toFixed(0)}€`}
            </p>
          </div>
          <Link to={`/devis/${stats.prochain_evenement.numero}`} className="ml-auto" style={{ color: 'var(--teal)' }}><ArrowUpRight size={18}/></Link>
        </div>
      )}

      {/* Carte fidélité */}
      <CarteFidelite fidelite={stats?.fidelite} loading={loading} />

      {/* Accès rapides */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { to: '/creer-evenement', icon: <Calendar size={16}/>, label: 'Créer un événement', color: 'var(--teal)' },
          { to: '/catalogue', icon: <Package size={16}/>, label: 'Voir le catalogue', color: '#5DBCBF' },
          { to: '/boutique', icon: <ShoppingBag size={16}/>, label: 'Boutique', color: '#1F9D7A' },
          { to: '/mon-organisation', icon: <Users size={16}/>, label: 'Mon organisation', color: '#C58B19' },
          { to: '/packs', icon: <Star size={16}/>, label: 'Packs événementiels', color: '#D4AF37' },
          { to: '/contact', icon: <MapPin size={16}/>, label: 'Nous contacter', color: '#7DB1FF' },
        ].map((item, i) => (
          <Link key={i} to={item.to}
            className="flex items-center gap-2.5 p-3 rounded-xl transition-all hover:shadow-sm"
            style={{ background: '#fff', border: `1px solid ${item.color}20` }}>
            <span style={{ color: item.color }}>{item.icon}</span>
            <span className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Onglet Devis & Factures ────────────────────────────────────────────────
function TabDevisFactures({ quotes, orders, loading, token }) {
  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6">
      {/* Devis */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--teal)' }}>
          <FileText size={16}/> Mes devis & réservations ({quotes.length})
        </h3>
        {quotes.length === 0 ? (
          <div className="p-8 rounded-2xl text-center" style={{ background: '#fff', border: '1px solid var(--border)' }}>
            <FileText size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }}/>
            <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>Aucun devis pour le moment</p>
            <Link to="/creer-evenement" className="btn-teal px-5 py-2.5 rounded-xl text-sm font-semibold inline-flex">Créer mon événement</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {quotes.map(q => {
              const cfg = STATUT_CONFIG[q.statut] || STATUT_CONFIG.brouillon;
              const dateEv = q.session?.date_evenement;
              return (
                <div key={q.id} className="p-4 rounded-2xl" style={{ background: '#fff', border: `1px solid ${cfg.color}20` }}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{q.numero}</span>
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background: cfg.bg, color: cfg.color }}>
                          {cfg.icon}{cfg.label}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Créé le {formatDate(q.created_at)}
                        {dateEv && ` · Événement le ${formatDate(dateEv + 'T00:00:00')}`}
                      </p>
                      {q.items?.length > 0 && (
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                          {q.items.slice(0,2).map(i => i.product_nom || i.nom || 'Article').join(', ')}
                          {q.items.length > 2 ? ` +${q.items.length - 2} articles` : ''}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="price-gold text-base font-bold">{q.total?.toFixed(2)}€</p>
                      {q.acompte > 0 && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Acompte : {q.acompte?.toFixed(2)}€</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 flex-wrap" style={{ borderTop: '1px solid var(--border)' }}>
                    <Link to={`/ma-reservation/${q.id}`} data-testid={`compte-suivi-${q.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-80"
                      style={{ background: 'var(--teal)', color: '#fff' }}>
                      <FileText size={12}/> Suivi réservation
                    </Link>
                    <Link to={`/devis/${q.id}`} data-testid={`compte-voir-devis-${q.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-80"
                      style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                      <FileText size={12}/> Voir le devis
                    </Link>
                    {canInvoice(q.statut) && (
                      <button onClick={() => openProtectedDoc(BACKEND, `/api/devis/${q.id}/facture`, token)}
                        data-testid={`compte-facture-${q.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-80"
                        style={{ background: 'rgba(212,175,55,0.1)', color: '#7A5E00', border: '1px solid rgba(212,175,55,0.3)', cursor: 'pointer' }}>
                        <Download size={12}/> Télécharger la facture
                      </button>
                    )}
                    {q.statut === 'signe' && (
                      <Link to={`/devis/${q.id}`} data-testid={`compte-payer-acompte-${q.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-80"
                        style={{ background: 'rgba(31,157,122,0.1)', color: '#1F9D7A', border: '1px solid rgba(31,157,122,0.25)' }}>
                        <Euro size={12}/> Payer l'acompte
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Commandes boutique */}
      {orders.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--teal)' }}>
            <ShoppingBag size={16}/> Commandes boutique ({orders.length})
          </h3>
          <div className="space-y-3">
            {orders.map(o => {
              const cfg = ORDER_STATUT[o.statut] || ORDER_STATUT.en_attente_paiement;
              return (
                <div key={o.id} className="p-4 rounded-2xl" style={{ background: '#fff', border: `1px solid ${cfg.color}20` }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{o.numero}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatDate(o.created_at)}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {(o.items||[]).slice(0,2).map(i => i.nom).join(', ')}
                        {(o.items||[]).length > 2 ? ` +${o.items.length-2}` : ''}
                      </p>
                    </div>
                    <p className="price-gold font-bold">{o.total?.toFixed(2)}€</p>
                  </div>
                  {canOrderInvoice(o.statut) && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                      <button onClick={() => openProtectedDoc(BACKEND, `/api/pdf/boutique/${o.id}/facture`, token)}
                        data-testid={`compte-facture-boutique-${o.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold w-fit hover:opacity-80"
                        style={{ background: 'rgba(212,175,55,0.1)', color: '#7A5E00', border: '1px solid rgba(212,175,55,0.3)', cursor: 'pointer' }}>
                        <Download size={12}/> Télécharger la facture
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Carte : Mes bons d'avoir (intempéries) ──────────────────────────────────
function MesBonsAvoir({ bonsAvoir }) {
  if (!bonsAvoir || !bonsAvoir.nombre) return null;
  return (
    <div className="p-5 rounded-2xl" data-testid="bons-avoir-card"
      style={{ background: 'linear-gradient(135deg, #0D2B3E 0%, #123447 100%)', border: '1px solid rgba(96,165,250,0.35)', color: '#fff' }}>
      <div className="flex items-center gap-2 mb-1">
        <Gift size={18} style={{ color: '#60A5FA' }} />
        <h3 className="font-semibold">Mes bons d'avoir</h3>
      </div>
      <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
        Émis en cas d'annulation pour intempéries · utilisables sur une prochaine réservation
      </p>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-3xl font-bold" style={{ color: '#60A5FA' }} data-testid="bons-avoir-solde">{(bonsAvoir.solde || 0).toFixed(2)}€</p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Disponible</p>
        </div>
        <div>
          <p className="text-2xl font-bold">{(bonsAvoir.total_emis || 0).toFixed(2)}€</p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Total émis</p>
        </div>
      </div>
      <div className="space-y-1">
        {(bonsAvoir.entries || []).map((h, i) => (
          <div key={i} className="flex items-center justify-between py-2"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }} data-testid={`bon-avoir-${i}`}>
            <div style={{ minWidth: 0 }}>
              <p className="text-sm font-medium">{h.raison || 'Bon d\'avoir'}</p>
              {h.date && <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{formatDateFR(h.date)} · sans expiration</p>}
              {h.pdf_path && (
                <a href={`${BACKEND}${h.pdf_path}`} target="_blank" rel="noreferrer"
                  data-testid={`bon-avoir-pdf-${i}`}
                  className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: '#93C5FD' }}>
                  <Download size={12}/> Télécharger l'attestation
                </a>
              )}
            </div>
            <p className="text-sm font-bold whitespace-nowrap" style={{ color: '#60A5FA' }}>+{(h.montant || 0).toFixed(2)}€</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Onglet Fidélité ─────────────────────────────────────────────────────────
function TabFidelite({ stats, loading }) {
  if (loading) return <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>;
  const fidelite = stats?.fidelite;

  return (
    <div className="space-y-5">
      <CarteFidelite fidelite={fidelite} loading={loading} />

      <MesBonsAvoir bonsAvoir={stats?.bons_avoir} />

      {/* Détail des paliers */}
      <div className="p-5 rounded-2xl" style={{ background: '#fff', border: '1px solid var(--border)' }}>
        <h3 className="font-semibold mb-4" style={{ color: 'var(--ink)' }}>Programme de fidélité</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {PALIERS.map((p, i) => {
            const active = fidelite?.palier?.min_locations === p.min;
            return (
              <div key={i} className="p-3 rounded-xl text-center" style={{
                background: active ? `${p.color}15` : 'rgba(0,0,0,0.02)',
                border: active ? `2px solid ${p.color}40` : '1px solid var(--border)',
              }}>
                <p className="text-xl mb-1">{p.icon}</p>
                <p className="text-sm font-bold" style={{ color: p.color }}>{p.label}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.pct > 0 ? `-${p.pct}%` : 'Standard'}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>dès {p.min} loc.</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4 p-3 rounded-xl" style={{ background: 'rgba(31,157,122,0.06)', border: '1px solid rgba(31,157,122,0.2)' }}>
          <p className="text-sm font-semibold" style={{ color: '#1F9D7A' }}>💰 Cashback automatique : 5% de chaque location</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Crédité après chaque paiement. Utilisable sur votre prochaine réservation.</p>
        </div>
      </div>

      {/* Stats fidélité */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-2xl text-center" style={{ background: '#fff', border: '1px solid var(--border)' }}>
          <p className="text-2xl font-bold price-gold">{fidelite?.cashback_disponible?.toFixed(2) || '0.00'}€</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Cashback disponible</p>
        </div>
        <div className="p-4 rounded-2xl text-center" style={{ background: '#fff', border: '1px solid var(--border)' }}>
          <p className="text-2xl font-bold" style={{ color: '#C58B19' }}>{fidelite?.cashback_cumule?.toFixed(2) || '0.00'}€</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total cashback cumulé</p>
        </div>
      </div>

      {/* Historique de la cagnotte (dont remises de compensation) */}
      {(fidelite?.historique || []).length > 0 && (
        <div className="p-5 rounded-2xl" style={{ background: '#fff', border: '1px solid var(--border)' }}>
          <h3 className="font-semibold mb-3" style={{ color: 'var(--ink)' }}>Détail de la cagnotte</h3>
          <div className="space-y-1">
            {[...fidelite.historique].reverse().map((h, i) => {
              const isComp = h.type === 'compensation';
              const label = isComp ? '🎁 Remise de compensation'
                : h.type === 'credit_manuel' ? 'Crédit'
                : h.type === 'cashback' ? 'Cashback (5%)' : 'Mouvement';
              return (
                <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }} data-testid={`fidelite-hist-${i}`}>
                  <div style={{ minWidth: 0 }}>
                    <p className="text-sm font-medium" style={{ color: isComp ? '#C58B19' : 'var(--ink)' }}>{label}</p>
                    {h.raison && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{h.raison}</p>}
                    {h.date && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatDateFR(h.date)}{h.expiration ? ` · expire le ${formatDateFR(h.expiration)}` : ' · sans expiration'}</p>}
                  </div>
                  <p className="text-sm font-bold whitespace-nowrap" style={{ color: '#1F9D7A' }}>+{(h.montant || 0).toFixed(2)}€</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onglet Parrainage ───────────────────────────────────────────────────────
function TabParrainage({ user, profilComplet }) {
  const [parrainageStats, setParrainageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (!user || !profilComplet) { setLoading(false); return; }
    fetch(`${BACKEND}/api/parrainage/stats/${user.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setParrainageStats(d)).catch(() => {})
      .finally(() => setLoading(false));
  }, [user, token, profilComplet]);

  const parrainage_code = parrainageStats?.code || '---';
  const lien = `${window.location.origin}?ref=${parrainage_code}`;

  function copyLink() {
    navigator.clipboard?.writeText(lien).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Lien copié !'); });
  }

  // Bloquer si profil incomplet
  if (!profilComplet) {
    return (
      <div className="p-6 rounded-2xl text-center" style={{ background: 'rgba(197,139,25,0.06)', border: '2px dashed rgba(197,139,25,0.3)' }}>
        <AlertCircle size={36} className="mx-auto mb-3" style={{ color: '#C58B19' }} />
        <h3 className="font-semibold mb-2" style={{ color: 'var(--ink)' }}>Profil incomplet</h3>
        <p className="text-sm mb-4" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Pour accéder au programme de parrainage et toucher vos gains,<br/>
          vous devez renseigner votre <strong>téléphone</strong> et votre <strong>adresse complète</strong>.
        </p>
        <Button
          onClick={() => document.querySelector('[data-testid="tab-parametres"]')?.click()}
          style={{ background: '#C58B19', color: '#fff' }}>
          Compléter mon profil →
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="p-5 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(93,188,191,0.08), rgba(212,175,55,0.06))', border: '1px solid rgba(93,188,191,0.2)' }}>
        <div className="text-3xl text-center mb-3">🎉</div>
        <h3 className="font-display text-lg font-bold text-center mb-2" style={{ color: 'var(--ink)' }}>Parrainez et gagnez</h3>
        <p className="text-sm text-center" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Partagez votre lien et gagnez <strong style={{ color: 'var(--teal)' }}>jusqu'à 15%</strong> sur chaque commande réalisée grâce à vous.
        </p>
      </div>

      {/* Code + lien */}
      <div className="p-4 rounded-2xl" style={{ background: '#fff', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>VOTRE CODE PARRAINAGE</p>
        <p className="text-2xl font-mono font-bold mb-3" style={{ color: 'var(--teal)' }}>{parrainage_code}</p>
        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Votre lien unique :</p>
        <div className="flex gap-2">
          <input readOnly value={lien} className="flex-1 text-xs px-3 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
          <button onClick={copyLink} data-testid="parrainage-copy-link-btn" className="px-3 py-2 rounded-lg flex items-center gap-1.5 text-xs font-semibold"
            style={{ background: copied ? 'rgba(31,157,122,0.15)' : 'var(--teal)', color: copied ? '#1F9D7A' : '#fff' }}>
            <Copy size={12}/>{copied ? 'Copié !' : 'Copier'}
          </button>
        </div>
      </div>

      {/* Stats */}
      {parrainageStats && (
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-xl text-center" style={{ background: '#fff', border: '1px solid var(--border)' }}>
            <p className="text-xl font-bold" style={{ color: 'var(--teal)' }}>{parrainageStats.clicks || 0}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Clics</p>
          </div>
          <div className="p-3 rounded-xl text-center" style={{ background: '#fff', border: '1px solid var(--border)' }}>
            <p className="text-xl font-bold" style={{ color: '#D4AF37' }}>{parrainageStats.conversions || 0}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Filleuls</p>
          </div>
          <div className="p-3 rounded-xl text-center" style={{ background: '#fff', border: '1px solid var(--border)' }}>
            <p className="text-xl font-bold" style={{ color: '#1F9D7A' }}>{(parrainageStats.wallet_balance || 0).toFixed(2)}€</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Crédit gagné</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Carte : changer mon mot de passe ────────────────────────────────────────
function ChangePasswordCard({ token, highlight }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (next.length < 8) { toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères'); return; }
    if (next !== confirm) { toast.error('Les deux mots de passe ne correspondent pas'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await res.text().then(t => t ? JSON.parse(t) : {});
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success('Mot de passe mis à jour ✓');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) { toast.error(err.message || 'Erreur'); }
    setSaving(false);
  }

  return (
    <div className="p-5 rounded-2xl" data-testid="change-password-card"
      style={{ background: '#fff', border: `1px solid ${highlight ? 'rgba(197,139,25,0.5)' : 'var(--border)'}` }}>
      <h3 className="font-semibold mb-1" style={{ color: 'var(--ink)' }}>Changer mon mot de passe</h3>
      <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
        {highlight
          ? 'Vous utilisez un mot de passe temporaire. Choisissez votre propre mot de passe.'
          : 'Mettez à jour votre mot de passe de connexion.'}
      </p>
      <div className="space-y-3">
        <Input type="password" value={current} onChange={e => setCurrent(e.target.value)} placeholder="Mot de passe actuel (ou temporaire)" data-testid="cp-current" />
        <Input type="password" value={next} onChange={e => setNext(e.target.value)} placeholder="Nouveau mot de passe (8 caractères min.)" data-testid="cp-new" />
        <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirmer le nouveau mot de passe" data-testid="cp-confirm" />
        <Button onClick={submit} disabled={saving} data-testid="cp-submit" style={{ background: 'var(--teal)', color: '#fff' }}>
          {saving ? <span className="animate-spin mr-2">⟳</span> : null}
          Enregistrer le mot de passe
        </Button>
      </div>
    </div>
  );
}

// ─── Onglet Paramètres ────────────────────────────────────────────────────────
function TabParametres({ user, token, onProfileSaved }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    prenom: user?.prenom || '',
    nom: user?.nom || '',
    telephone: user?.telephone || '',
    adresse_personnelle: user?.adresse_personnelle || '',
  });
  const [saving, setSaving] = useState(false);

  // Sync quand user change (ex: reload)
  useEffect(() => {
    setForm({
      prenom: user?.prenom || '',
      nom: user?.nom || '',
      telephone: user?.telephone || '',
      adresse_personnelle: user?.adresse_personnelle || '',
    });
  }, [user]);

  const profilComplet = !!(user?.telephone?.trim() && user?.adresse_personnelle?.trim());

  async function saveProfile() {
    if (!form.telephone?.trim()) { toast.error('Le téléphone est obligatoire'); return; }
    if (!form.adresse_personnelle?.trim()) { toast.error("L'adresse est obligatoire"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/me/update`, {
        method: 'PUT',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.text().then(t => t ? JSON.parse(t) : {});
      if (res.ok && data.success) {
        toast.success('Profil mis à jour !');
        setEditing(false);
        if (onProfileSaved) onProfileSaved(data.user);
      } else {
        throw new Error(data.detail || 'Erreur');
      }
    } catch (err) { toast.error(err.message || 'Erreur sauvegarde'); }
    setSaving(false);
  }

  const PROFIL_LABELS = { particulier: 'Particulier', entreprise: 'Entreprise', association: 'Association', ce: 'CE', groupe: 'Partenaire' };

  return (
    <div className="space-y-5">
      {/* Alerte profil incomplet */}
      {!profilComplet && (
        <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ background: 'rgba(229,62,62,0.05)', border: '2px solid rgba(229,62,62,0.2)' }}>
          <AlertCircle size={20} style={{ color: '#e53e3e', flexShrink: 0, marginTop: 1 }} />
          <div>
            <p className="font-semibold text-sm" style={{ color: '#e53e3e' }}>Profil incomplet</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Votre <strong>téléphone</strong> et votre <strong>adresse</strong> sont obligatoires pour commander et toucher le parrainage.
            </p>
            {!editing && (
              <button onClick={() => setEditing(true)} className="mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: '#e53e3e', color: '#fff' }}>
                Compléter maintenant
              </button>
            )}
          </div>
        </div>
      )}

      {/* Infos profil */}
      <div className="p-5 rounded-2xl" style={{ background: '#fff', border: `1px solid ${profilComplet ? 'var(--border)' : 'rgba(229,62,62,0.2)'}` }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold" style={{ color: 'var(--ink)' }}>Informations personnelles</h3>
          <button onClick={() => setEditing(e => !e)} data-testid="compte-profile-edit-btn" className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg"
            style={{ background: editing ? 'rgba(229,62,62,0.08)' : 'rgba(93,188,191,0.1)', color: editing ? '#e53e3e' : 'var(--teal)' }}>
            <Edit2 size={12}/> {editing ? 'Annuler' : 'Modifier'}
          </button>
        </div>

        <div className="space-y-4">
          {/* Prénom + Nom */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs mb-1 font-medium" style={{ color: 'var(--text-muted)' }}>Prénom</p>
              {editing
                ? <Input value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} placeholder="Votre prénom" />
                : <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{user?.prenom || '—'}</p>}
            </div>
            <div>
              <p className="text-xs mb-1 font-medium" style={{ color: 'var(--text-muted)' }}>Nom</p>
              {editing
                ? <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Votre nom" />
                : <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{user?.nom || '—'}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <p className="text-xs mb-1 font-medium" style={{ color: 'var(--text-muted)' }}>Email</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{user?.email}</p>
          </div>

          {/* Téléphone — OBLIGATOIRE */}
          <div>
            <p className="text-xs mb-1 font-medium flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
              Téléphone <span style={{ color: '#e53e3e' }}>*</span>
              {!user?.telephone && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(229,62,62,0.1)', color: '#e53e3e' }}>Requis</span>}
            </p>
            {editing
              ? <Input type="tel" value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                  placeholder="06 XX XX XX XX" style={{ borderColor: !form.telephone ? 'rgba(229,62,62,0.4)' : undefined }} />
              : <p className="text-sm font-semibold" style={{ color: user?.telephone ? 'var(--ink)' : '#e53e3e' }}>
                  {user?.telephone || '⚠ Non renseigné'}
                </p>}
          </div>

          {/* Adresse personnelle — OBLIGATOIRE */}
          <div>
            <p className="text-xs mb-1 font-medium flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
              Adresse de domicile <span style={{ color: '#e53e3e' }}>*</span>
              {!user?.adresse_personnelle && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(229,62,62,0.1)', color: '#e53e3e' }}>Requise</span>}
            </p>
            {editing
              ? <Input value={form.adresse_personnelle} onChange={e => setForm(f => ({ ...f, adresse_personnelle: e.target.value }))}
                  placeholder="Numéro, rue, code postal, ville"
                  style={{ borderColor: !form.adresse_personnelle ? 'rgba(229,62,62,0.4)' : undefined }} />
              : <p className="text-sm font-semibold" style={{ color: user?.adresse_personnelle ? 'var(--ink)' : '#e53e3e' }}>
                  {user?.adresse_personnelle || '⚠ Non renseignée'}
                </p>}
            {editing && (
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                💡 L'adresse de livraison de vos événements peut être différente (à renseigner dans chaque devis).
              </p>
            )}
          </div>

          {/* Type de compte */}
          <div>
            <p className="text-xs mb-1 font-medium" style={{ color: 'var(--text-muted)' }}>Type de compte</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--teal)' }}>{PROFIL_LABELS[user?.profile] || 'Particulier'}</p>
          </div>
        </div>

        {editing && (
          <div className="flex gap-2 mt-5 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
            <Button onClick={saveProfile} disabled={saving} data-testid="compte-profile-save-btn" style={{ background: 'var(--teal)', color: '#fff' }}>
              {saving ? <span className="animate-spin mr-2">⟳</span> : <Save size={13} className="mr-1.5"/>}
              Enregistrer
            </Button>
            <Button onClick={() => setEditing(false)} variant="outline">Annuler</Button>
          </div>
        )}
      </div>

      {/* Changer mon mot de passe */}
      <ChangePasswordCard token={token} highlight={!!user?.must_change_password} />

      {/* Accès rapides */}
      <div className="p-5 rounded-2xl space-y-3" style={{ background: '#fff', border: '1px solid var(--border)' }}>
        <h3 className="font-semibold mb-3" style={{ color: 'var(--ink)' }}>Navigation</h3>
        <Link to="/mon-organisation" data-testid="compte-organisation-link" className="flex items-center justify-between p-3 rounded-xl hover:opacity-80"
          style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
          <div className="flex items-center gap-2">
            <Users size={16} style={{ color: 'var(--teal)' }}/>
            <span className="text-sm font-medium" style={{ color: 'var(--ink)' }}>Mon espace organisation</span>
          </div>
          <ChevronRight size={14} style={{ color: 'var(--text-muted)' }}/>
        </Link>
        <button onClick={() => { logout(); navigate('/'); }} data-testid="compte-logout-btn"
          className="flex items-center gap-2 p-3 rounded-xl w-full text-left hover:opacity-80"
          style={{ background: 'rgba(229,62,62,0.05)', border: '1px solid rgba(229,62,62,0.15)' }}>
          <LogOut size={16} style={{ color: '#e53e3e' }}/>
          <span className="text-sm font-medium" style={{ color: '#e53e3e' }}>Se déconnecter</span>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE MON COMPTE
// ═══════════════════════════════════════════════════════════════════════════
export default function MonComptePage() {
  const { user: authUser, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  // Permet la mise à jour en temps réel du profil sans rechargement
  const [localUser, setLocalUser] = useState(null);
  const user = localUser || authUser;

  const profilComplet = !!(user?.telephone?.trim() && user?.adresse_personnelle?.trim());
  const perks = PROFILE_PERKS[user?.profile];

  const loadAll = useCallback(async () => {
    if (!authUser || !token) return;
    setStatsLoading(true);
    setDataLoading(true);
    try {
      const [statsRes, quotesRes, ordersRes] = await Promise.all([
        fetch(`${BACKEND}/api/auth/me/stats`, { cache: 'no-store', headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${BACKEND}/api/devis?client_id=${authUser.id}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        authUser.email ? fetch(`${BACKEND}/api/boutique/commandes/client/${encodeURIComponent(authUser.email)}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()) : Promise.resolve({ orders: [] }),
      ]);
      setStats(statsRes);
      setQuotes(quotesRes.quotes || []);
      setOrders(ordersRes.orders || []);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setStatsLoading(false);
    setDataLoading(false);
  }, [authUser, token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  function handleProfileSaved(updatedUser) {
    setLocalUser(updatedUser);
  }

  const TABS = [
    { id: 'dashboard',  label: 'Tableau de bord', icon: <BarChart2 size={14}/> },
    { id: 'devis',      label: 'Devis & Factures', icon: <FileText size={14}/>, badge: quotes.length },
    ...(perks ? [{ id: 'avantages', label: 'Mes avantages', icon: <Award size={14}/> }] : []),
    { id: 'fidelite',   label: 'Fidélité',         icon: <Star size={14}/> },
    { id: 'parrainage', label: 'Parrainage',        icon: <Gift size={14}/> },
    { id: 'parametres', label: 'Paramètres',        icon: <Settings size={14}/>, alert: !profilComplet },
  ];

  if (!user) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose text-center" style={{ paddingTop: '8rem' }}>
        <User size={48} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
        <p className="text-lg font-semibold mb-4" style={{ color: 'var(--ink)' }}>Connectez-vous pour accéder à votre compte</p>
        <Link to="/connexion" className="btn-teal px-6 py-3 rounded-xl font-semibold inline-flex">Se connecter</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '6rem' }}>

        {/* Bannière mot de passe temporaire */}
        {user?.must_change_password && (
          <div className="flex items-center gap-3 p-4 rounded-2xl mb-4" data-testid="must-change-password-banner"
            style={{ background: 'rgba(197,139,25,0.08)', border: '1px solid rgba(197,139,25,0.35)' }}>
            <AlertCircle size={20} style={{ color: '#C58B19', flexShrink: 0 }} />
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: '#C58B19' }}>Mot de passe temporaire</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Vous êtes connecté avec un mot de passe temporaire. Choisissez votre propre mot de passe.
              </p>
            </div>
            <button onClick={() => setActiveTab('parametres')}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0"
              style={{ background: '#C58B19', color: '#fff' }}>
              Changer →
            </button>
          </div>
        )}

        {/* Bannière profil incomplet */}
        {!profilComplet && (
          <div className="flex items-center gap-3 p-4 rounded-2xl mb-4"
            style={{ background: 'rgba(229,62,62,0.05)', border: '1px solid rgba(229,62,62,0.25)' }}>
            <AlertCircle size={20} style={{ color: '#e53e3e', flexShrink: 0 }} />
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: '#e53e3e' }}>Profil incomplet</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Ajoutez votre <strong>téléphone</strong> et votre <strong>adresse</strong> pour commander et accéder au parrainage.
              </p>
            </div>
            <button onClick={() => setActiveTab('parametres')}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0"
              style={{ background: '#e53e3e', color: '#fff' }}>
              Compléter →
            </button>
          </div>
        )}

        {/* Header profil */}
        <div className="flex items-center gap-4 p-5 rounded-2xl mb-6" style={{ background: '#fff', border: '1px solid var(--border)' }}>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold flex-shrink-0"
            style={{ background: 'var(--teal-mist)', color: 'var(--teal)' }}>
            {(user.prenom || user.nom || user.email || 'U')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-lg" style={{ color: 'var(--ink)' }}>
              {user.prenom && user.nom ? `${user.prenom} ${user.nom}` : (user.nom || user.prenom || 'Mon compte')}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
            {user.telephone && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>📞 {user.telephone}</p>
            )}
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'var(--teal-mist)', color: 'var(--teal)' }}>
              {stats?.fidelite?.palier?.icon} {stats?.fidelite?.palier?.label || 'Nouveau client'}
            </p>
            {perks && (
              <p className="text-xs font-bold px-2.5 py-1 rounded-full mt-1" data-testid="profile-remise-badge"
                style={{ background: `${perks.color}18`, color: perks.color }}>
                {perks.label} · -{perks.remise}%
              </p>
            )}
            {stats && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Membre depuis {formatDate(user.created_at || stats.membre_depuis || '')}</p>}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-5">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium flex-shrink-0 transition-all relative"
              style={activeTab === tab.id
                ? { background: 'var(--teal)', color: '#fff', boxShadow: '0 2px 8px rgba(93,188,191,0.3)' }
                : { background: '#fff', color: 'var(--text-muted)', border: `1px solid ${tab.alert ? 'rgba(229,62,62,0.4)' : 'var(--border)'}` }}>
              {tab.icon}
              {tab.label}
              {tab.alert && activeTab !== tab.id && (
                <span className="w-2 h-2 rounded-full absolute top-1 right-1" style={{ background: '#e53e3e' }} />
              )}
              {tab.badge > 0 && (
                <span className="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ background: activeTab === tab.id ? 'rgba(255,255,255,0.3)' : 'var(--teal)', color: '#fff', fontSize: 10 }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Contenu onglets */}
        {activeTab === 'dashboard'  && <TabDashboard stats={stats} loading={statsLoading} />}
        {activeTab === 'devis'      && <TabDevisFactures quotes={quotes} orders={orders} loading={dataLoading} token={token} />}
        {activeTab === 'avantages'  && <TabAvantages perks={perks} />}
        {activeTab === 'fidelite'   && <TabFidelite stats={stats} loading={statsLoading} />}
        {activeTab === 'parrainage' && <TabParrainage user={user} profilComplet={profilComplet} />}
        {activeTab === 'parametres' && <TabParametres user={user} token={token} onProfileSaved={handleProfileSaved} />}
      </div>
      <Footer />
    </div>
  );
}
